<template>
  <div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {{ companyName ? `Sign in to ${companyName}` : 'Sign in to your account' }}
        </h2>
        <p class="mt-2 text-center text-sm text-gray-600">
          Or
          <router-link :to="{ name: 'sign-up' }" class="font-medium text-indigo-600 hover:text-indigo-500">
            create a new account
          </router-link>
        </p>
      </div>

      <div v-if="isNimbleAI" class="my-5">
        <a :href="googleSignInUrl" class="w-full bg-blue-500 text-white hover:bg-blue-600 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded-md py-2 px-4 flex items-center justify-center">
          <i class="fab fa-google mr-2" /> Sign In with Google
        </a>
      </div>

      <div v-if="isNimbleAI" class="text-center text-3xl font-bold">
        OR
      </div>

      <form class="mt-8 space-y-6" @submit.prevent="handleSignIn">
        <div v-if="error" class="rounded-md bg-red-50 p-4 mb-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800">{{ error }}</h3>
            </div>
          </div>
        </div>
        <div class="rounded-md shadow-sm -space-y-px">
          <div>
            <label for="email-address" class="sr-only">Email address</label>
            <input id="email-address" name="email" type="email" autocomplete="email" required
              v-model="email"
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Email address" />
          </div>
          <div>
            <label for="password" class="sr-only">Password</label>
            <input id="password" name="password" type="password" autocomplete="current-password" required
              v-model="password"
              class="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Password" />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <input id="remember-me" name="remember-me" type="checkbox" 
              v-model="rememberMe"
              class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" />
            <label for="remember-me" class="ml-2 block text-sm text-gray-900">
              Remember me
            </label>
          </div>

          <div class="text-sm">
            <a href="#" class="font-medium text-indigo-600 hover:text-indigo-500">
              Forgot your password?
            </a>
          </div>
        </div>

        <div>
          <button type="submit" 
            :disabled="isLoading"
            :class="[
              'group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500',
              isLoading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
            ]">
            <span v-if="isLoading" class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="animate-spin h-5 w-5 text-indigo-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
            <span v-else class="absolute left-0 inset-y-0 flex items-center pl-3">
              <svg class="h-5 w-5 text-indigo-500 group-hover:text-indigo-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
              </svg>
            </span>
            {{ isLoading ? 'Signing in...' : 'Sign in' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import AuthService from '../services/AuthService';
import AppUtil from "@/app/utils/AppUtil.js";

export default {
  name: 'SignIn',
  data() {
    return {
      email: '',
      password: '',
      rememberMe: false,
      isLoading: false,
      error: null,
      authService: new AuthService()
    }
  },
  computed: {
    companyName() {
      return this.model?.whitelabelConfig?.company_name || null;
    },
    model() {
      return window.nimbleai?.model || {};
    },
    baseUrl() {
      return window.location.host.indexOf("localhost") !== -1
          ? 'http://localhost:3000'
          : 'https://www.nimbleai.co'
    },
    googleSignInUrl() {
      let url = this.baseUrl + '/google_sign_in/initialize'

      if(this.model.intendedRoute) {
        url = url + '?return_to=' + encodeURIComponent(this.model.intendedRoute.path)
      }

      return url
    },
    isNimbleAI() {
      return window.location.host.indexOf("www.nimbleai.co") !== -1
          || window.location.host.indexOf("localhost") !== -1;
    }
  },
  methods: {
    handleSignIn() {
      this.isLoading = true;
      this.error = null;
      
      this.authService.login(this.email, this.password)
        .then(user => {
          // Store user in global state
          if (this.model && typeof this.model === 'object') {
            this.model.user = user;
          }
          
          // Check for intended route and redirect there if it exists
          if (this.model.intendedRoute) {
            const intendedPath = this.model.intendedRoute.path || '/';
            // Add query parameters if they exist
            let redirectUrl = intendedPath;
            if (this.model.intendedRoute.query) {
              const queryParams = new URLSearchParams(this.model.intendedRoute.query).toString();
              if (queryParams) {
                redirectUrl = `${intendedPath}?${queryParams}`;
              }
            }
            
            // Clear the stored route
            this.model.intendedRoute = null;
            
            // Redirect with full page reload
            window.location.href = redirectUrl;
          } else {
            // Default redirect if no intended route was stored
            window.location.href = '/';
          }
        })
        .catch(error => {
          console.error('Login error:', error);
          this.error = error.message || 'Invalid email or password. Please try again.';
        })
        .finally(() => {
          this.isLoading = false;
        });
    }
  }
}
</script>
